/**
 * Public landing page — mobile-first (320–430px primary target), single h1.
 *
 * Section order: header, hero, trust strip, #features, #how-it-works, #verify,
 * #use-cases (+ "what you can prove"), final CTA, #faq, footer.
 * Copy is governed by docs/eidas-claims-policy-2026-09-11.md (tier T0).
 */
import { Link } from "react-router-dom";
import { LandingHeader } from "../components/landing/LandingHeader";
import { HeroProofDiagram } from "../components/landing/HeroProofDiagram";
import {
  Eyebrow,
  FinalCta,
  ProcessTimeline,
  ProtectionLayers,
  TrustStrip,
  UseCases,
} from "../components/landing/Sections";
import { VerificationSection } from "../components/landing/VerificationDiagram";
import { FAQ } from "../components/landing/FAQAccordion";
import { LandingFooter } from "../components/landing/LandingFooter";
import { Icon } from "../components/landing/icons";

const HERO_POINTS = [
  "Sealed against alteration",
  "Cryptographic evidence chain",
  "Independently verifiable proof of time",
] as const;

export function Landing() {
  return (
    <div className="flex min-h-full flex-col overflow-x-clip bg-white">
      <LandingHeader />

      <main id="main" className="flex-1">
        {/* Hero */}
        <section aria-labelledby="hero-title" className="bg-white pb-14 pt-10 sm:pb-20 sm:pt-16 lg:pb-24 lg:pt-20">
          <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-center lg:gap-12">
            <div>
              <Eyebrow>Trusted digital evidence</Eyebrow>
              <h1
                id="hero-title"
                className="text-[2.375rem] leading-[1.05] sm:text-[3rem] lg:text-[3.5rem] xl:text-[4rem]"
              >
                Digital evidence you can prove
              </h1>
              <p className="mt-5 max-w-prose text-base leading-relaxed text-ink-soft sm:text-lg">
                Turn photos, videos, and files into verifiable evidence. StampmatS seals each finalised
                record against alteration, links it into a cryptographic evidence chain, and creates an
                independently verifiable proof of time.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/signup" className="btn-primary min-h-[48px] w-full sm:w-auto">
                  Create account
                </Link>
                <a href="#how-it-works" className="btn-secondary min-h-[48px] w-full sm:w-auto">
                  See how it works
                </a>
              </div>

              <ul className="mt-8 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-x-6" aria-label="Key protections">
                {HERO_POINTS.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-sm font-medium text-ink">
                    <Icon name="check" size={18} className="shrink-0 text-aigentico-navy" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>

            <HeroProofDiagram />
          </div>
        </section>

        <TrustStrip />
        <ProtectionLayers />
        <ProcessTimeline />
        <VerificationSection />
        <UseCases />
        <FinalCta />
        <FAQ />
      </main>

      <LandingFooter />
    </div>
  );
}
